import { BFF_BASE_URL, SESSION_STORAGE_KEY } from './config';
import { useSessionStore, isTokenExpired } from '../stores/sessionStore';

export function getStoredToken(): string | null {
  try {
    const raw = window.localStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const token = parsed?.access_token || parsed?.token || null;
    if (token && isTokenExpired(token)) {
      window.localStorage.removeItem(SESSION_STORAGE_KEY);
      return null;
    }
    return token;
  } catch {
    return null;
  }
}

const ACTIVE_TENANT_ID_KEY = 'crm_active_tenant_id';

export function normalizeTenantId(raw?: string | null): string | null {
  if (!raw) return null;
  const first = String(raw).split(',')[0].trim();
  if (
    first === 'Inteligência e Negócios Tecnologia' ||
    first === 'Inteligencia e Negocios Tecnologia' ||
    first === 'inteligencia-e-negocios' ||
    first === 'vanguarda'
  ) {
    return 'vanguarda';
  }
  return first;
}

export function getActiveTenantId(): string | null {
  try {
    const raw = window.localStorage.getItem(ACTIVE_TENANT_ID_KEY);
    return normalizeTenantId(raw);
  } catch {
    return null;
  }
}

export function getActiveLanguage(): string {
  try {
    const raw = (typeof window !== 'undefined' ? window.localStorage.getItem('i18nextLng') : '') || '';
    if (raw.toLowerCase().startsWith('en')) return 'en';
    return 'pt-BR';
  } catch {
    return 'pt-BR';
  }
}

export function getAuthHeaders(token?: string) {
  const effectiveToken = token || getStoredToken() || '';
  const activeTenant = getActiveTenantId();
  const activeLang = getActiveLanguage();
  return {
    Authorization: effectiveToken ? `Bearer ${effectiveToken}` : '',
    'Content-Type': 'application/json',
    'x-platform-language': activeLang,
    ...(activeTenant ? { 'x-tenant-id': activeTenant } : {}),
  };
}

export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const token = getStoredToken();
  const activeTenant = getActiveTenantId();
  const headers = new Headers(init?.headers);

  const isAuthEndpoint =
    path.includes('/login') ||
    path.includes('/forgot-password') ||
    path.includes('/reset-password');

  if (!headers.has('Authorization') && token && !isAuthEndpoint) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  if (!isAuthEndpoint) {
    if (activeTenant && !headers.has('x-tenant-id')) {
      headers.set('x-tenant-id', activeTenant);
    }
    if (!headers.has('x-platform-language')) {
      headers.set('x-platform-language', getActiveLanguage());
    }
  }
  const isFormData =
    init?.body &&
    ((typeof FormData !== 'undefined' && init.body instanceof FormData) ||
     (init.body as any)?.constructor?.name === 'FormData' ||
     typeof (init.body as any)?.append === 'function');

  if (isFormData) {
    headers.delete('Content-Type');
    headers.delete('content-type');
  } else if (!headers.has('Content-Type') && init?.method && init.method !== 'GET') {
    headers.set('Content-Type', 'application/json');
  }


  const response = await fetch(`${BFF_BASE_URL}${path}`, {
    ...init,
    headers,
  });
  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    if (response.status === 401) {
      const isAuthEndpoint =
        path.includes('/login') ||
        path.includes('/forgot-password') ||
        path.includes('/reset-password');

      if (!isAuthEndpoint) {
        console.warn(`[HTTP 401] Sessão expirada ao acessar ${path}. Limpando dados e redirecionando para login.`);
        useSessionStore.getState().clear();

        if (typeof window !== 'undefined') {
          const currentPath = window.location.pathname;
          if (
            !currentPath.startsWith('/login') &&
            !currentPath.startsWith('/forgot-password') &&
            !currentPath.startsWith('/reset-password')
          ) {
            window.location.href = '/login';
          }
        }
      }
    }

    const message =
      (data as { message?: string }).message ||
      (data as { error?: string }).error ||
      'Falha na requisição';
    throw new Error(`${response.status}: ${message}`);
  }

  return data as T;
}
